import React, { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import AuthLayout from "../components/AuthLayout"
import { getAPIBaseURL, getSiteBaseURL } from "../services/helpers"
import { postData } from "../services/request"
import { getApiErrorMessage } from "../services/apiErrorMessage"
import { validateEmail, validatePassword } from "../services/validators"

const UserLogin = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [btnState, setBtnState] = useState("Sign in")
  const [error, setError] = useState([
    { field: "email", msg: "" },
    { field: "password", msg: "" }
  ])
  const [genError, setGenError] = useState("")

  const validateForm = () => {
    let ok = true
    const errs = [...error]
    const eVal = validateEmail(email)
    const pVal = validatePassword(password)
    errs[errs.findIndex(i => i.field == "email")].msg = eVal.result
    errs[errs.findIndex(i => i.field == "password")].msg = pVal.result
    if (eVal.error != "") ok = false
    if (pVal.error != "") ok = false
    setError(errs)
    return ok
  }

  const login = async () => {
    setGenError("")
    if (!validateForm()) return
    setBtnState("Loading...")
    const url = `${getAPIBaseURL()}/v1/users/login`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('email', email)
    params.append('password', password)
    const requestResult = await postData(url, headers, params.toString())
    setBtnState("Sign in")
    if (requestResult.error == "") {
      if (requestResult.result.status === 200) {
        try {
          // Get user data from response - handle different possible response structures
          const userData = requestResult.result.data?.data || requestResult.result.data?.user || requestResult.result.data
          //const token = requestResult.result.data?.token
          
          // Store user in localStorage
          if (userData) {
            localStorage.setItem('user', JSON.stringify(userData))
            console.log("User stored in localStorage:", userData)
          } else {
            console.warn("No user data found in response:", requestResult.result.data)
          }
          
          // Store token if available
          /*if (token) {
            localStorage.setItem('token', token)
            console.log("Token stored in localStorage")
          }*/
          
          // Set active session
          sessionStorage.setItem('active', true)
          
          // Verify user was stored correctly
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null')
          if (!storedUser) {
            console.error("Failed to store user in localStorage")
            setGenError("Failed to save user session. Please try again.")
            return
          }
          
          // Navigate based on user role
          if(storedUser?.role === 'HOSPITAL'){
            console.log("Logged in as hospital, navigating to hospital dashboard")
            navigate('/hospital/dashboard')
          } else {
            console.log("Logged in as patient, navigating to patient dashboard")
            navigate('/dashboard')
          }
        } catch (e) {
          console.error("Error logging in:", e)
          setGenError("An error occurred during login. Please try again.")
        }
      } else {
        console.log("Invalid credentials", requestResult.result.data?.message)
        setGenError(getApiErrorMessage(requestResult, 'Invalid credentials'))
      }
    } else {
      console.log("Login failed", requestResult.result?.data?.message)
      setGenError(getApiErrorMessage(requestResult, 'Login failed'))
    }
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to continue to Medpool.">
      <div className="space-y-5">
        {genError != "" ? <div className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-500">{genError}</div> : ""}

        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-900">Email</label>
          <input value={email} onChange={(e)=>setEmail(e.target.value)} type="email" id="email" className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-600 focus:ring-primary-600" placeholder="name@example.com" />
          {error.find(i=>i.field=="email").msg ? <p className="mt-1 text-sm font-semibold text-red-500">{error.find(i=>i.field=="email").msg}</p> : null}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="password" className="text-sm font-medium text-gray-900">Password</label>
            <Link to="/forgot-password" className="text-sm font-medium text-blue-600 hover:underline">Forgot password?</Link>
          </div>
          <div className="relative">
            <input
              value={password}
              onChange={(e)=>setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              id="password"
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-sm text-gray-900 focus:border-primary-600 focus:ring-primary-600"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-800"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.03-10-7 0-.87.255-1.69.7-2.435m3.364-2.95C7.502 6.208 9.675 5 12 5c5.523 0 10 4.03 10 7 0 .87-.255 1.69-.7 2.435m-3.364 2.95A10.05 10.05 0 0112 19c-1.415 0-2.755-.283-3.975-.8" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4l16 16" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          {error.find(i=>i.field=="password").msg ? <p className="mt-1 text-sm font-semibold text-red-500">{error.find(i=>i.field=="password").msg}</p> : null}
        </div>

        <button onClick={()=>login()} type="button" className="w-full rounded-lg bg-black px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300">{btnState}</button>

        <p className="text-sm font-light text-gray-500">
          Don’t have an account? <Link to="/signup/patient" className="font-medium text-blue-600 hover:underline">Create one</Link>
        </p>
      </div>
    </AuthLayout>
  )
}

export default UserLogin


